import { withActionDialogLock } from "@/action-dialog/actionDialogLock";
import { ConfirmTrashCallable } from "@/action-dialog/callables";
import { undoRestoreSkillErrorMessage, useRestoreSkill } from "@/api/archived";
import { useDeleteSkill } from "@/api/skills";
import { showMovedToTrashToast } from "@/components/ConfirmTrashDialog/movedToTrashToast";
import { useAppNavigate } from "@/hooks/pane-routing";
import {
  useRailSelection,
  type RailRegisteredResource,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { archiveSkillsSettled } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/skills/archiveSkillsSettled";
import {
  useMultiSelectionObjectActionHandlers,
  useObjectActions,
} from "@/object-actions";
import type { Skill } from "@cohesive/api-client";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";

function selectedSkillsFrom(resources: RailRegisteredResource[]): Skill[] {
  return resources.filter((r) => r.kind === "skill").map((r) => r.data);
}

/**
 * Multi-select bulk trash for the Skills rail.
 */
export function useSkillsRailMultiSelectionActions() {
  const selection = useRailSelection();
  const appNavigate = useAppNavigate();
  const deleteSkill = useDeleteSkill();
  const restoreSkill = useRestoreSkill();

  const selectedSkills = useMemo(
    () => selectedSkillsFrom(selection.getSelectedResources()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selection.selectedIds, selection.selectionCount],
  );

  const handleBulkDelete = useCallback(() => {
    if (selectedSkills.length < 2) return;
    const skills = selectedSkills;
    void withActionDialogLock(() =>
      ConfirmTrashCallable.call({
        kind: "skill",
        name: `${skills.length} skills`,
        mutationFn: async (call) => {
          // Settle every row — rejecting on the first failure used to leave
          // earlier skills archived while the dialog stayed open and
          // selection uncleared (no `call.end`).
          const { archived, failed } = await archiveSkillsSettled(
            skills,
            deleteSkill,
          );

          if (archived.length === 0) {
            toast.error(
              failed === 1
                ? "Failed to archive skill."
                : `Failed to archive ${failed} skills.`,
            );
            // Keep the dialog open for retry (same as single-skill trash).
            throw new Error("delete failed");
          }

          if (failed > 0) {
            toast.error(`Archived ${archived.length}; ${failed} failed`);
          }

          showMovedToTrashToast(() => {
            for (const skill of archived) {
              restoreSkill.mutate(
                { skillId: skill.id },
                {
                  onError: (err) =>
                    toast.error(undoRestoreSkillErrorMessage(err)),
                },
              );
            }
          });
          selection.clear();
          appNavigate({ pane: "rightPane", to: null });
          call.end(true);
        },
      }),
    );
  }, [selectedSkills, deleteSkill, restoreSkill, selection, appNavigate]);

  const multiSelectionHandlers = useMultiSelectionObjectActionHandlers({
    onOpen: () => {},
    onRename: () => {},
    onBulkMove: () => {},
    onDuplicate: () => {},
    onShare: () => {},
    onBulkDelete: handleBulkDelete,
    canOpen: false,
    canRename: false,
    canMove: false,
    canDuplicate: false,
    canShare: false,
    canDelete: selectedSkills.length > 1,
  });

  const actionState = {
    selectionSingle: false,
    canRenameSelection: false,
    canDuplicateSelection: false,
    canShare: false,
    hasChatsInSelection: false,
  };

  const { actions: selectionBarActions } = useObjectActions({
    kind: { type: "multi-selection", kinds: [] },
    surface: "selection-bar",
    enabled: selection.selectionCount > 1,
    state: actionState,
    handlers: multiSelectionHandlers,
  });

  const { actions: leftRailMultiActions } = useObjectActions({
    kind: { type: "multi-selection", kinds: [] },
    surface: "left-rail",
    enabled: selection.selectionCount > 1,
    state: actionState,
    handlers: multiSelectionHandlers,
  });

  return {
    selectionCount: selection.selectionCount,
    selectionBarActions,
    leftRailMultiActions,
    clear: selection.clear,
    clearIfOutside: selection.clearIfOutside,
  };
}
